import { Button, ComboboxItem, Flex, Group, Modal, Select, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { useAdvisors } from 'src/clients/hooks/useAdvisors';
import { useReassignFamilyAdvisor } from 'src/clients/hooks/useReassignFamilyAdvisor';
import { Logger } from 'src/utils/Logger';

type Props = {
  familyId: string;
  currentAdvisorId?: string;
  opened: boolean;
  close: () => void;
  onTransferSuccess?: () => void;
};

export default function TransferFamilyModal({
  familyId,
  currentAdvisorId,
  opened,
  close,
  onTransferSuccess,
}: Props) {
  const { advisors, isLoading: advisorsLoading } = useAdvisors();
  const [value, setValue] = useState<ComboboxItem | null>(null);
  const {
    reassignFamilyAdvisor,
    isLoading: isReassigning,
    error: reassignError,
    reset,
  } = useReassignFamilyAdvisor();

  const currentAdvisor = advisors.find(advisor => advisor.id === currentAdvisorId);

  const handleSelectAdvisor = (_value: string | null, option: ComboboxItem) => {
    reset();
    setValue(option);
  };

  const updateFamilyAdvisor = async () => {
    if (!value) return;
    try {
      await reassignFamilyAdvisor(familyId, value.value);
      close();
      notifications.show({
        title: 'Transfer successful',
        message: `This family has been successfully assigned to ${value.label}`,
        color: 'green',
      });
      onTransferSuccess?.();
    } catch (e) {
      Logger.error('Failed to transfer family', e);
    }
  };

  return (
    <Modal opened={opened} onClose={close} title="Transfer family" centered>
      <Flex direction="column" gap="md">
        <Text mb="md">
          Once the transfer is complete, the current advisor may lose access to this family
          depending on their user role.
        </Text>
        {currentAdvisor && (
          <Text mb="md">
            <Text span fw={700}>
              Current advisor:
            </Text>{' '}
            {currentAdvisor.lastName}, {currentAdvisor.firstName}
          </Text>
        )}
        <Select
          label="Select new advisor"
          placeholder="Select advisor"
          data={advisors.map(advisor => ({
            label: `${advisor.lastName}, ${advisor.firstName}`,
            value: advisor.id,
          }))}
          value={value ? value.value : null}
          onChange={handleSelectAdvisor}
          searchable
          required
          error={reassignError ? 'Failed to transfer family. Please try again.' : undefined}
        />
        <Group justify="flex-end" mt="md">
          <Button
            type="submit"
            variant="filled-pill-black"
            loading={advisorsLoading || isReassigning}
            onClick={updateFamilyAdvisor}
            disabled={!value || isReassigning}
          >
            Transfer
          </Button>
        </Group>
      </Flex>
    </Modal>
  );
}
